import type { ValidationSchema } from '@/types';
import * as v from 'valibot';

type Messages = string[];

type RecordFromSchema<S, V> = Record<keyof S, V>;

type ResultOutput<S> = {
  valid: boolean;
  output: S;
  statuses: RecordFromSchema<S, boolean>;
  messages: RecordFromSchema<S, Messages>;
};

export default class Valibot {
  private static Output<S>(schema: ValidationSchema, data: S): S {
    const result = v.safeParse(schema, data);
    return result.output as S;
  }

  private static ErrorsList<S>(schema: ValidationSchema, data: S): RecordFromSchema<S, Messages> {
    const list = Valibot.RecordFromSchema<S, Messages>(schema, []);
    const result = v.safeParse(schema, data);
    if (!result.success) {
      const nested = v.flatten(result.issues).nested ?? {};
      for (const [key, messages] of Object.entries(nested)) {
        list[key as keyof S] = messages as string[];
      }
    }
    return list;
  }

  public static RecordFromSchema<S, V>(schema: ValidationSchema, value: V): RecordFromSchema<S, V> {
    const record = {} as RecordFromSchema<S, V>;
    for (const key of Object.keys(schema.entries)) {
      record[key as keyof S] = value;
    }
    return record;
  }

  public static Result<S>(schema: ValidationSchema, data: S): ResultOutput<S> {
    const messages = Valibot.ErrorsList<S>(schema, data);

    const valid = (Object.values(messages) as Messages[]).every((message) => message.length === 0);

    const statuses = {} as RecordFromSchema<S, boolean>;
    for (const key of Object.keys(messages)) {
      statuses[key as keyof S] = messages[key as keyof S].length > 0;
    }

    return {
      valid,
      output: Valibot.Output(schema, data),
      statuses,
      messages,
    };
  }
}
